// @ts-check

import { getPayslipInputExpenses } from '@/api/payslipInputs'
import MarkAsPaidDialog from '@/components/entryCard/MarkAsPaidDialog.vue'
import SalaryApprovalDialog from '@/components/salaries/dialogs/SalaryApprovalDialog.vue'
import SalaryDeletionDialog from '@/components/salaries/dialogs/SalaryDeletionDialog.vue'
import SalaryVoidingDialog from '@/components/salaries/dialogs/SalaryVoidingDialog.vue'
import { useDialog } from '@/core/composables/useDialog'
import { SALARY_EDIT, SALARY_LIST } from '@/router/salaryRoutes'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useToasts } from '../useToasts'

/**
 * @typedef Options
 * @property {Function} [onUpdate]
 */

/**
 * @param {Options} options
 */
export const usePayslipInputActions = (options = {}) => {
  const { showDialog } = useDialog()
  const { addDangerToast } = useToasts()
  const { t } = useI18n()
  const router = useRouter()

  /**
   * @param {import('vue').Component} dialog
   * @param {any} listeners
   * @returns {(payslipInput: ReferenceInterface|PayslipInput) => void}
   */
  const showPayslipInputDialog =
    (dialog, listeners = {}, props = {}) =>
    (payslipInput) =>
      showDialog({
        component: dialog,
        props: {
          payslipInputId: payslipInput.id,
          ...props
        },
        listeners
      })

  /**
   * Navigates to the edit payslip input view/page.
   * @param {ReferenceInterface|PayslipInput} payslipInput
   */
  const navigateToEditPayslipInput = async (payslipInput) => {
    await router.push({
      name: SALARY_EDIT.name,
      params: { id: payslipInput.id }
    })
  }

  /**
   * Navigates to payslip input list view/page.
   */
  const navigateToPayslipInputs = async () => {
    await router.push({ name: SALARY_LIST.name })
  }

  /**
   * Shows payslip input deletion dialog.
   */
  const showPayslipInputDeletionDialog = showPayslipInputDialog(
    SalaryDeletionDialog,
    {
      onDeleted: options.onUpdate
    }
  )

  /**
   * Shows payslip input invalidation dialog.
   */
  const showPayslipInputInvalidationDialog = showPayslipInputDialog(
    SalaryVoidingDialog,
    {
      onVoided: options.onUpdate
    }
  )

  /**
   * Show payslip input approval dialog.
   */
  const showPayslipInputApprovalDialog = showPayslipInputDialog(
    SalaryApprovalDialog,
    {
      onApproved: options.onUpdate
    }
  )

  /**
   * Show payslip input expense payment dialog.
   * @param {ReferenceInterface|PayslipInput} payslipInput
   *
   * @TODO MarkAsPaidDialog should only require entry id so this function could be simplified.
   */
  const showPayslipInputPaymentDialog = async (payslipInput) => {
    try {
      const expenses = await getPayslipInputExpenses(payslipInput.id)

      if (expenses.length !== 1) {
        throw new Error('Invalid number of expenses!')
      }

      const expense = expenses[0]

      showPayslipInputDialog(
        MarkAsPaidDialog,
        {
          onSuccess: options.onUpdate,
          onError: () => addDangerToast({ content: t('development_error') })
        },
        {
          id: expense.id,
          entry: expense,
          type: expense.type,
          total: expense.total_with_vat,
          paid: expense.payment_transactions_total,
          outstanding: expense.outstanding_sum ?? 0
        }
      )(payslipInput)
    } catch (error) {
      console.error(error)
      addDangerToast({ content: t('development_error') })
    }
  }

  return {
    showPayslipInputDeletionDialog,
    showPayslipInputInvalidationDialog,
    showPayslipInputApprovalDialog,
    showPayslipInputPaymentDialog,
    navigateToEditPayslipInput,
    navigateToPayslipInputs
  }
}

export default usePayslipInputActions
